import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container } from "reactstrap";
import { removeLoadingBay, retrieveLoadingBays } from "utils/api";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import { isEmpty } from "lodash";
import useStoreFilters from "customHooks/useStoreFilters";
import { FILTER_PAGE } from "utils/constants";
import { loadingBaysListColumns } from "pages/stock-management/loadingBays/LoadingBaysListColumns";
import LoadingBaysSearch from "pages/stock-management/loadingBays/LoadingBaysSearch";
import CustomLink from "components/Common/buttons/CustomLink";
import CustomButton from "components/Common/buttons/CustomButton";
import NewLBModal from "pages/stock-management/loadingBays/newLoadingBay/NewLBModal";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";

const LoadingBaysList = () => {
  const [isLoading, setLoader] = useState(false);
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [removeItem, setRemoveItem] = useState(null);
  const { filters, setFilters } = useStoreFilters(FILTER_PAGE.LB);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setActiveId(null);
  }, []);

  const openModal = useCallback(() => setModalOpen(true), []);

  useEffect(() => {
    !isEmpty(filters) ? handleSearch(filters) : handleSearch();
  }, []);

  const onListLoaded = useCallback(res => {
    const {
      result: { items },
    } = res?.data;
    if (items?.length === 0) {
      warnMessage("Loading Bays were not found");
    }
    setList(res?.data || null);

    setLoader(false);
  }, []);

  const handleSearch = (filterOptions = {}) => {
    setLoader(true);
    setFilters(filterOptions);
    retrieveLoadingBays(filterOptions)
      .then(res => onListLoaded(res))
      .catch(err => {
        setLoader(false);
        setList([]);
        defaultNgiErrorMethod(err);
      });
  };

  const onDelete = useCallback(() => {
    setLoader(true);
    closeDeleteModal();
    removeLoadingBay(removeItem?.id)
      .then(() => {
        setTimeout(() => {
          setLoader(false);
          handleSearch();
          successMessage("Loading Bay deleted successfully");
        }, 3000);
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [removeItem]);

  const editLb = useCallback(id => {
    setActiveId(id);
    openModal();
  }, []);

  const openDeleteModal = useCallback(item => setRemoveItem(item), []);
  const closeDeleteModal = useCallback(() => setRemoveItem(null), []);

  const columns = useMemo(() => loadingBaysListColumns(editLb, openDeleteModal), [editLb, openDeleteModal]);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className="filters-wrapper">
            <div className="filter-buttons">
              <CustomLink className="margin-right">
                <CustomButton
                  onClick={openModal}
                  icon={<i className="bx bx-plus-circle" />}
                  type="primary"
                  text="NEW LOADING BAY"
                />
              </CustomLink>
            </div>

            <LoadingBaysSearch handleSearch={handleSearch} />
          </div>
          <Col lg={12}>
            <>
              <NGITable
                filtersType={FILTER_PAGE.LB}
                isLoading={isLoading}
                columns={columns}
                data={list}
                updateList={handleSearch}
              />
            </>
          </Col>
        </Container>
        {modalOpen && <NewLBModal onClose={closeModal} update={handleSearch} id={activeId} />}
        {removeItem && (
          <ConfirmationModal
            description={`Are you sure you want to delete Loading Bay ${removeItem?.name}?`}
            onCancel={closeDeleteModal}
            onConfirm={onDelete}
          />
        )}
      </div>
    </>
  );
};

export default LoadingBaysList;
